/**
 * Lot 35 — Teacher views.
 *
 * /teacher/classes               — list of classes the teacher is assigned to
 * /teacher/classes/:classId      — class detail with students table
 *
 * ACL: open to any authenticated user; the backend filters by teacher
 * memberships and returns 403 on classes that aren't theirs. A non-teacher
 * just sees an empty list.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Users, ClipboardList, RefreshCcw, Plus,
  GraduationCap, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { teacherApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SUPPORT_META = {
  on_track: { fr: 'En forme', icon: CheckCircle2, cls: 'text-emerald-300' },
  needs_reinforcement: { fr: 'À renforcer', icon: AlertTriangle, cls: 'text-amber-300' },
  needs_help: { fr: 'À aider', icon: XCircle, cls: 'text-rose-300' },
};

const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

// ---------------------------------------------------------------------------
// LIST: /teacher/classes
// ---------------------------------------------------------------------------
export const TeacherClassesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await teacherApi.classes();
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login', { replace: true }); return; }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  if (authLoading || loading) return <PageLoading />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="teacher-classes-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/')}
              className="mb-3" data-testid="teacher-classes-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Button>
      <SectionHeader
        overline="Console professeur"
        title="Mes classes"
        subtitle="Vos classes assignées dans les madrassas partenaires."
      />
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="ghost" onClick={refresh}
                data-testid="teacher-classes-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
      </div>

      {items.length === 0 ? (
        <GlassCard data-testid="teacher-classes-empty" className="mt-4">
          <div className="text-center py-6">
            <BookOpen className="w-8 h-8 text-text-muted mx-auto mb-2" />
            <p className="text-sm text-text-muted">
              Vous n&apos;avez pas encore de classe assignée. L&apos;administrateur
              de votre madrassa peut vous ajouter à une classe depuis sa console.
            </p>
          </div>
        </GlassCard>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
             data-testid="teacher-classes-list">
          {items.map((c) => (
            <GlassCard key={c.classId}
                       data-testid={`teacher-class-card-${c.classId}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/teacher/classes/${c.classId}`}
                        className="text-sm font-medium text-text-primary hover:text-primary"
                        data-testid={`teacher-class-card-${c.classId}-name`}>
                    {c.className}
                  </Link>
                  <p className="text-[11px] text-text-muted mt-0.5">
                    {c.madrassaName}{c.level ? ` · ${c.level}` : ''}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-text-secondary">
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {c.studentsCount} élèves
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <ClipboardList className="w-3 h-3" /> {c.activeAssignments} devoirs
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => navigate(`/teacher/classes/${c.classId}`)}
                        data-testid={`teacher-class-card-${c.classId}-open`}>
                  Voir élèves
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => navigate(`/devoirs?createForClass=${c.classId}`)}
                        data-testid={`teacher-class-card-${c.classId}-create-assignment`}>
                  <Plus className="w-3 h-3 mr-1" /> Devoir
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DETAIL: /teacher/classes/:classId
// ---------------------------------------------------------------------------
export const TeacherClassDetailPage = () => {
  const navigate = useNavigate();
  const { classId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [cls, setCls] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, sRes] = await Promise.all([
        teacherApi.classDetail(classId),
        teacherApi.classStudents(classId),
      ]);
      setCls(cRes.data);
      setStudents(sRes.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Classe inaccessible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login', { replace: true }); return; }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, classId]);

  if (authLoading || loading) return <PageLoading />;
  if (error || !cls) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
           data-testid="teacher-class-detail-error">
        <Button variant="ghost" size="sm"
                onClick={() => navigate('/teacher/classes')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <p className="text-sm text-rose-300 mt-4">{error || 'Classe inaccessible.'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="teacher-class-detail-page">
      <Button variant="ghost" size="sm"
              onClick={() => navigate('/teacher/classes')}
              className="mb-3" data-testid="teacher-class-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à mes classes
      </Button>

      <GlassCard data-testid="teacher-class-detail-header">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif text-text-primary"
                data-testid="teacher-class-detail-name">{cls.className}</h2>
            <p className="text-[11px] text-text-muted mt-1">
              {cls.madrassaName}{cls.level ? ` · ${cls.level}` : ''} · {cls.studentsCount} élèves
            </p>
            {cls.description && (
              <p className="text-sm text-text-secondary mt-2">{cls.description}</p>
            )}
          </div>
          <Button size="sm"
                  onClick={() => navigate(`/devoirs?createForClass=${classId}`)}
                  data-testid="teacher-class-detail-create-assignment">
            <Plus className="w-3.5 h-3.5 mr-1" /> Créer un devoir
          </Button>
        </div>
      </GlassCard>

      <div className="mt-4 space-y-2" data-testid="teacher-class-students-list">
        {students.length === 0 && (
          <GlassCard data-testid="teacher-class-students-empty">
            <p className="text-sm text-text-muted text-center py-4">
              Aucun élève dans cette classe.
            </p>
          </GlassCard>
        )}
        {students.map((s) => (
          <StudentRow key={s.userId} s={s} />
        ))}
      </div>
    </div>
  );
};

const StudentRow = ({ s }) => {
  const a = s.assignmentStats || {};
  const p = s.progressSummary || {};
  const supportMeta = SUPPORT_META[s.supportStatus] || SUPPORT_META.on_track;
  const Icon = supportMeta.icon;
  return (
    <GlassCard data-testid={`teacher-student-row-${s.userId}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <GraduationCap className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text-primary truncate"
                  data-testid={`teacher-student-row-${s.userId}-name`}>
              {s.name || s.email || s.userId}
            </span>
            <span className={`inline-flex items-center gap-1 text-[11px] ${supportMeta.cls}`}
                  data-testid={`teacher-student-row-${s.userId}-support`}>
              <Icon className="w-3 h-3" /> {supportMeta.fr}
            </span>
          </div>
          <p className="text-[11px] text-text-muted mt-0.5">
            {s.email}{s.lastActivityAt ? ` · activité : ${fmtDate(s.lastActivityAt)}` : ''}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-text-secondary flex-wrap">
            <span data-testid={`teacher-student-row-${s.userId}-assigned`}>
              <ClipboardList className="w-3 h-3 inline" /> {a.completed ?? 0}/{a.assigned ?? 0} devoirs
            </span>
            <span data-testid={`teacher-student-row-${s.userId}-avg`}>
              Score moyen : {a.averageScore != null ? `${a.averageScore}%` : '—'}
            </span>
            <span>
              Lecture : {p.readingProgressPercent != null ? `${p.readingProgressPercent}%` : '—'}
            </span>
            <span>
              Tajwīd : {p.tajweedProgressPercent != null ? `${p.tajweedProgressPercent}%` : '—'}
            </span>
          </div>
          {(p.weakSkills?.length > 0 || p.weakTajweedRules?.length > 0) && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.weakSkills || []).map((sk, i) => (
                <Badge key={`r-${i}`}
                       className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px]">
                  {sk}
                </Badge>
              ))}
              {(p.weakTajweedRules || []).map((r, i) => (
                <Badge key={`t-${i}`}
                       className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[10px]">
                  {r}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

export default TeacherClassesPage;
